import { Component, HostListener } from '@angular/core';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { ChartConfiguration } from 'chart.js';
import moment from 'moment';
import { DataserviceService } from '../dataservice.service';
import { NzTableSortFn } from 'ng-zorro-antd/table';
import { HttpParams } from '@angular/common/http';
import { ChatService } from '../services/chat.service';
import { NotificationChannel } from '../chat/chat.types';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Component({
  selector: 'app-leaderboard',
  templateUrl: './leaderboard.component.html',
  styleUrl: './leaderboard.component.css',
  animations: [
    trigger('slideDown', [
      transition(':enter', [
        style({ opacity: 0, height: 0, transform: 'translateY(-10px)' }),
        animate('300ms ease-out', style({ opacity: 1, height: '*', transform: 'translateY(0)' }))
      ]),
      transition(':leave', [
        animate('300ms ease-in', style({ opacity: 0, height: 0, transform: 'translateY(-10px)' }))
      ])
    ]),
    trigger('expandCollapse', [
      transition(':enter', [
        style({ opacity: 0, height: 0, overflow: 'hidden' }),
        animate('300ms ease-out', style({ opacity: 1, height: '*' }))
      ]),
      transition(':leave', [
        animate('300ms ease-in', style({ opacity: 0, height: 0, overflow: 'hidden' }))
      ])
    ])
  ]
})
export class LeaderboardComponent {

  isMobile: boolean = false;
  userScores: any = [];
  currentUser: any;
  isAdmin: boolean = false;
  recordLoading: boolean = false;
  verbiage = ''
  metricsData: any;
  metricsLabels: string[] = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  metricsOptions: ChartConfiguration['options'] = {
    responsive: true,
  };
  topStats = [
    { value: 0, title: 'Total Sales', subStats: [{ label: 'Jan', value: 0 }, { label: 'Feb', value: 0 }] },
    { value: 0, title: 'Total Completions', subStats: [{ label: 'Jan', value: 0 }, { label: 'Feb', value: 0 }] },
    { value: 0, title: 'Total Cancels', subStats: [{ label: 'Jan', value: 0 }, { label: 'Feb', value: 0 }] },
    { value: 0, title: 'Total Pending', subStats: [{ label: 'Jan', value: 0 }, { label: 'Feb', value: 0 }] }
  ];

  leaderboardData = [];
  selectedDateRange: [Date, Date] = [new Date(), new Date()];
  tempDateRange: [Date, Date] = [new Date(), new Date()];
  showCustomDatePicker = false;
  isCustomDateModalVisible = false;
  isDateOptionsExpanded = false;
  selectedPeriod: string = 'today';
  isLoading = false;

  dateOptions = [
    { value: 'today', label: 'Today', icon: '☀️' },
    { value: 'yesterday', label: 'Yesterday', icon: '🌙' },
    { value: 'thisWeek', label: 'This Week', icon: '📅' },
    { value: 'lastWeek', label: 'Last Week', icon: '📆' },
    { value: 'thisMonth', label: 'This Month', icon: '📊' },
    { value: 'thisYear', label: 'This Year', icon: '🗓️' }
  ];
  currentUserBestSales: number = 0;
  currentUserBestSalesDate: string = '';
  currentUserBestInstalls: number = 0;
  currentUserBestInstallsDate: string = '';
  allTimeBestSales: number = 0;
  allTimeBestSalesDate: string = '';
  allTimeBestInstalls: number = 0;
  allTimeBestInstallsDate: string = '';

  // Collapsible card state
  expandedCards: Set<number> = new Set();

  // Sorting state
  leaderboardSortBy: string = 'total_orders';

  sortOptions = [
    { value: 'total_orders', label: 'Sales', icon: '📊' },
    { value: 'total_installs', label: 'Installs', icon: '✅' },
    { value: 'cancels', label: 'Cancels', icon: '❌' },
    { value: 'pending', label: 'Pending', icon: '⏳' },
    { value: 'pullThrough', label: 'Completion', icon: '🎯' },
    { value: 'multiplier', label: 'Multiplier', icon: '✨' },
    { value: 'score', label: 'Score', icon: '💯' }
  ];
  private readonly salesMilestone = 10;
  private readonly managerPermissions = ['view_imports', 'view_groups', 'view_users', 'view_companies', 'manage_markets'];
  private readonly milestoneStorageKey = 'leaderboard_sales_milestones';
  private notifiedMilestoneUserIds = new Set<string>();
  private managerUserIds: string[] = [];
  private managersLoaded = false;
  private pendingMilestones = new Map<string, any>();


  constructor(
    private dataService: DataserviceService,
    private chatService: ChatService,
  ) {}

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.checkScreenSize();
  }

  ngOnInit() {
    this.setDateRange('today');
    this.checkScreenSize();
    const rawUser = localStorage.getItem('user');
    this.currentUser = rawUser ? JSON.parse(rawUser) : null;
    if (this.currentUser?.role?.name === 'Admin' || this.currentUser?.super_admin) {
      this.isAdmin = true;
    }
    this.restoreMilestoneState();
    this.loadManagers();
  }

  checkScreenSize() {
    this.isMobile = window.innerWidth < 768;
  }

  fetchData() {
    this.isLoading = true;
    this.dataService.getMetricsData('leaderboard/metrics', this.selectedDateRange).subscribe({
      next: (data: any) => {
        this.metricsData = data.metricsData;
        this.topStats = data.topStats;
        this.metricsLabels = data.metricsLabels;
      },
      error: (error) => {
        console.error('Error fetching data:', error);
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }

  recordBoard() {
    this.recordLoading = true;
    this.dataService.getData(`leaderboard/recordBoard`)
      .subscribe({
        next: (data: any) => {
          this.currentUserBestSales = data.currentUser.sales.value;
          this.currentUserBestSalesDate = data.currentUser.sales.date;
          this.currentUserBestInstalls = data.currentUser.installs.value;
          this.currentUserBestInstallsDate = data.currentUser.installs.date;
          this.allTimeBestSales = data.allTime.sales.value;
          this.allTimeBestSalesDate = data.allTime.sales.date;
          this.allTimeBestInstalls = data.allTime.installs.value;
          this.allTimeBestInstallsDate = data.allTime.installs.date;
        },
        error: (error) => {
          console.error('Error fetching data:', error);
        },
        complete: () => {
          this.recordLoading = false;
        }
      })
  }

  leaderBoard() {
    this.dataService.getMetricsData(`leaderboard/leaderboard-table`, this.selectedDateRange)
      .subscribe({
        next: (res: any) => {
          this.leaderboardData = res;
          console.log('leaderboardData: ', this.leaderboardData)
          this.setPerformance();
          this.checkSalesMilestones();

        } 
      })
  }

  setPerformance() {
    const qualifiedUsers = this.leaderboardData.filter((user: any) => user.total_installs > 10);
    const meanPullThroughRate = qualifiedUsers.length > 0 ? qualifiedUsers.reduce((sum, user) => sum + this.calculatePullThrough(user), 0) / qualifiedUsers.length : 0;
    this.userScores = this.leaderboardData.map((user: any) => {

      const pullThroughRate = this.calculatePullThrough(user)
      const deviation = (pullThroughRate * 100) - (meanPullThroughRate * 100);
      const multiplier = 1 + (deviation / 100);
      const score = user.total_installs * multiplier;

      return {
        firstName: user.first_name, 
        lastName: user.last_name,
        installs: user.total_installs || 0,
        pullThroughRate: pullThroughRate || 0,
        multiplier: multiplier || 0,
        score: score || 0
      };
    }).sort((a, b) => b.score - a.score);

  }

  setDateRange(range: string) {
    this.selectedPeriod = range;
    const today = moment();
    switch (range) {
      case 'today':
        this.verbiage = 'Today';
        this.selectedDateRange = [
          today.clone().startOf('day').toDate(),
          today.clone().endOf('day').toDate()
        ];
        break;
      case 'yesterday':
        this.verbiage = 'Yesterday';
        const yesterday = today.clone().subtract(1, 'days');
        this.selectedDateRange = [
          yesterday.clone().startOf('day').toDate(),
          yesterday.clone().endOf('day').toDate()
        ];
        break;
      case 'thisWeek':
        this.verbiage = 'This Week';
        this.selectedDateRange = [today.clone().startOf('week').toDate(), today.clone().endOf('week').toDate()];
        break;
      case 'lastWeek':
        this.verbiage = 'Last Week';
        const lastWeekStart = today.clone().subtract(1, 'weeks').startOf('week');
        const lastWeekEnd = today.clone().subtract(1, 'weeks').endOf('week');
        this.selectedDateRange = [lastWeekStart.toDate(), lastWeekEnd.toDate()];
        break;
      case 'thisMonth':
        this.verbiage = 'This Month';
        this.selectedDateRange = [today.clone().startOf('month').toDate(), today.clone().endOf('month').toDate()];
        break;
      case 'lastMonth':
        this.verbiage = 'Last Month';
        const lastMonthStart = today.clone().subtract(1, 'months').startOf('month');
        const lastMonthEnd = today.clone().subtract(1, 'months').endOf('month');
        this.selectedDateRange = [lastMonthStart.toDate(), lastMonthEnd.toDate()];
        break;
      case 'thisYear':
        this.verbiage = 'This Year';
        this.selectedDateRange = [today.clone().startOf('year').toDate(), today.clone().endOf('year').toDate()];
        break;
      case 'custom':
        return; // Don't fetch data yet, wait for modal
    }
    
    this.fetchData();
    this.recordBoard();
    this.leaderBoard();
  }

  toggleDateOptions() {
    this.isDateOptionsExpanded = !this.isDateOptionsExpanded;
  }

  selectDateOption(range: string) {
    this.setDateRange(range);
    // Auto-collapse after selection
    setTimeout(() => {
      this.isDateOptionsExpanded = false;
    }, 300);
  }

  openCustomDateModal() {
    this.tempDateRange = [...this.selectedDateRange] as [Date, Date];
    this.isCustomDateModalVisible = true;
  }

  handleCustomDateOk() {
    this.selectedDateRange = this.tempDateRange;
    this.selectedPeriod = 'custom';
    this.verbiage = this.selectedDateRange.map(date => moment(date).format('MMM D, YY')).join(' - ');
    this.isCustomDateModalVisible = false;
    // Auto-collapse after custom date selection
    setTimeout(() => {
      this.isDateOptionsExpanded = false;
    }, 300);
    this.fetchData();
    this.recordBoard();
    this.leaderBoard();
  }

  handleCustomDateCancel() {
    this.isCustomDateModalVisible = false;
  }

  calculatePullThrough(user: any): number {
    if(user?.total_installs) {
      return user.total_installs / (user.total_orders - user.pending);
    }else {
      return 0
    }
  }

  compareByPullThrough = (a: any, b: any): number => {
    const rateA = this.calculatePullThrough(a);
    const rateB = this.calculatePullThrough(b);
    return rateA - rateB;
  }

  sortFns: { [key: string]: NzTableSortFn<any> } = {
    name: (a: any, b: any) => a.name.localeCompare(b.name),
    total_orders: (a: any, b: any) => a.total_orders - b.total_orders,
    total_installs: (a: any, b: any) => (Number(a.total_installs) || 0) - (Number(b.total_installs) || 0),
    cancels: (a: any, b: any) => a.cancels - b.cancels,
    pending: (a: any, b: any) => a.pending - b.pending,
    pullThrough: (a: any, b: any) => this.calculatePullThrough(a) - this.calculatePullThrough(b),
    multiplier: (a: any, b: any) => this.getUserMultiplier(a) - this.getUserMultiplier(b),
    score: (a: any, b: any) => this.getUserScore(a) - this.getUserScore(b)
  };

  trackByIndex(index: number): number {
    return index;
  }

  // Toggle card expansion
  toggleCard(index: number): void {
    if (this.expandedCards.has(index)) {
      this.expandedCards.delete(index);
    } else {
      this.expandedCards.add(index);
    }
  }

  isCardExpanded(index: number): boolean {
    return this.expandedCards.has(index);
  }


  // Get sorted leaderboard data
  getSortedLeaderboardData(): any[] {
    const data = [...this.leaderboardData];
    return data.sort((a: any, b: any) => {
      switch (this.leaderboardSortBy) {
        case 'total_orders':
          return (b.total_orders || 0) - (a.total_orders || 0);
        case 'total_installs':
          return (b.total_installs || 0) - (a.total_installs || 0);
        case 'cancels':
          return (b.cancels || 0) - (a.cancels || 0);
        case 'pending':
          return (b.pending || 0) - (a.pending || 0);
        case 'pullThrough':
          return this.calculatePullThrough(b) - this.calculatePullThrough(a);
        case 'multiplier':
          return this.getUserMultiplier(b) - this.getUserMultiplier(a);
        case 'score':
          return this.getUserScore(b) - this.getUserScore(a);
        default:
          return 0;
      }
    });
  }


  // Get value for selected metric
  getLeaderboardMetricValue(user: any): any {
    switch (this.leaderboardSortBy) {
      case 'total_orders':
        return user.total_orders || 0;
      case 'total_installs':
        return user.total_installs || 0;
      case 'cancels':
        return user.cancels || 0;
      case 'pending':
        return user.pending || 0;
      case 'pullThrough':
        return this.calculatePullThrough(user);
      case 'multiplier':
        return this.getUserMultiplier(user);
      case 'score':
        return this.getUserScore(user);
      default:
        return 0;
    }
  }

  // Get label for selected metric
  getLeaderboardMetricLabel(): string {
    return this.sortOptions.find(opt => opt.value === this.leaderboardSortBy)?.label || '';
  }

  // Format metric value
  formatMetricValue(value: any, metric: string): string {
    if (metric === 'pullThrough' || metric === 'pullThroughRate') {
      return (value * 100).toFixed(0) + '%';
    } else if (metric === 'multiplier') {
      return value.toFixed(2) + 'x';
    } else if (metric === 'score') {
      return value.toFixed(1);
    }
    return value.toString();
  }

  // Calculate multiplier for a user
  getUserMultiplier(user: any): number {
    const qualifiedUsers = this.leaderboardData.filter((u: any) => u.total_installs > 10);
    const meanPullThroughRate = qualifiedUsers.length > 0 
      ? qualifiedUsers.reduce((sum, u) => sum + this.calculatePullThrough(u), 0) / qualifiedUsers.length 
      : 0;
    const pullThroughRate = this.calculatePullThrough(user);
    const deviation = (pullThroughRate * 100) - (meanPullThroughRate * 100);
    return 1 + (deviation / 100);
  }

  // Calculate score for a user
  getUserScore(user: any): number {
    const installs = user.total_installs || 0;
    const multiplier = this.getUserMultiplier(user);
    return installs * multiplier;
  }

  // Change sort
  changeLeaderboardSort(sortBy: string): void {
    this.leaderboardSortBy = sortBy;
  }

  private checkSalesMilestones(): void {
    if (!Array.isArray(this.leaderboardData) || !this.leaderboardData.length) {
      return;
    }

    const milestoneUsers = this.leaderboardData.filter((user: any) => {
      const milestoneUserId = this.extractUserId(user);
      const totalOrders = Number(user?.total_orders) || 0;
      return (
        milestoneUserId &&
        totalOrders >= this.salesMilestone &&
        !this.notifiedMilestoneUserIds.has(milestoneUserId)
      );
    });

    if (!milestoneUsers.length) {
      return;
    }

    if (!this.managersLoaded) {
      this.queuePendingMilestones(milestoneUsers);
      return;
    }

    this.sendMilestoneNotifications(milestoneUsers);
  }

  private queuePendingMilestones(users: any[]): void {
    users.forEach((user) => {
      const userId = this.extractUserId(user);
      if (userId && !this.pendingMilestones.has(userId)) {
        this.pendingMilestones.set(userId, user);
      }
    });
  }

  private flushPendingMilestones(): void {
    if (!this.pendingMilestones.size) {
      return;
    }
    const queued = Array.from(this.pendingMilestones.values());
    this.pendingMilestones.clear();
    this.sendMilestoneNotifications(queued);
  }

  private sendMilestoneNotifications(users: any[]): void {
    if (!users.length) {
      return;
    }

    if (!this.managerUserIds.length) {
      console.warn('No manager recipients available for milestone notifications.');
      users.forEach((user) => {
        const userId = this.extractUserId(user);
        if (userId) {
          this.markMilestoneNotified(userId);
        }
      });
      return;
    }

    users.forEach((user) => {
      const userId = this.extractUserId(user);
      if (!userId) {
        return;
      }
      const displayName =
        [user?.first_name, user?.last_name].filter(Boolean).join(' ') ||
        user?.email ||
        'A teammate';
      const totalOrders = Number(user?.total_orders) || this.salesMilestone;
      const metadata = {
        milestone: this.salesMilestone,
        totalOrders,
        leaderboardUserId: userId,
      };
      const title = `${displayName} hit ${this.salesMilestone} sales!`;
      const body = `${displayName} just reached ${totalOrders} total sales on the leaderboard.`;
      const recipients = this.managerUserIds.filter(
        (recipientId) => recipientId && recipientId !== userId,
      );

      if (!recipients.length) {
        this.markMilestoneNotified(userId);
        return;
      }

      const requests = recipients.map((recipientId) =>
        this.chatService
          .createNotification({
            recipientId,
            actorId: userId,
            type: 'leaderboard.sales.milestone',
            title,
            body,
            metadata,
            channels: [NotificationChannel.INBOX],
          })
          .pipe(
            catchError((error) => {
              console.warn('Failed to notify manager about sales milestone.', error);
              return of(null);
            }),
          ),
      );

      forkJoin(requests).subscribe({
        next: () => this.markMilestoneNotified(userId),
        error: () => this.markMilestoneNotified(userId),
      });
    });
  }

  private loadManagers(): void {
    if (!this.currentUser) {
      return;
    }
    const params = this.buildUserQueryParams();
    this.dataService.getData('users/users-list', { params }).subscribe({
      next: (response: any) => {
        const rows = Array.isArray(response)
          ? response
          : response?.data ??
            response?.items ??
            response?.records ??
            [];
        const managerIds = rows
          .filter((user: any) => this.isManagerUser(user))
          .map((user: any) => this.extractUserId(user))
          .filter((id: string | null): id is string => !!id);
        this.managerUserIds = Array.from(new Set(managerIds));
        this.managersLoaded = true;
        this.flushPendingMilestones();
      },
      error: (error) => {
        console.error('Unable to load managers for notifications.', error);
        this.managerUserIds = [];
        this.managersLoaded = true;
        this.flushPendingMilestones();
      },
    });
  }

  private buildUserQueryParams(): HttpParams {
    let params = new HttpParams();

    if (this.hasPermission('view_all_users')) {
      params = params.set('all', true);
    } else if (this.hasPermission('view_distributor_users') && this.currentUser?.distributor_id) {
      params = params.set('distributor_id', this.currentUser.distributor_id);
    } else if (this.hasPermission('view_company_users') && this.currentUser?.company_id) {
      params = params.set('company_id', this.currentUser.company_id);
    } else if (this.currentUser?.viewable_groups?.length) {
      params = params.set('viewable_groups', this.currentUser.viewable_groups);
    } else if (this.currentUser?.id) {
      params = params.set('my_id', this.currentUser.id);
    }

    return params;
  }

  private hasPermission(permission: string): boolean {
    if (this.currentUser?.super_admin) {
      return true;
    }
    const permissions = this.currentUser?.role?.permissions ?? [];
    return permissions.some((perm: any) => {
      const name = typeof perm === 'string' ? perm : perm?.name;
      return name === permission;
    });
  }

  private isManagerUser(user: any): boolean {
    if (!user) {
      return false;
    }
    if (user.super_admin) {
      return true;
    }
    const permissions = user.role?.permissions ?? user.permissions ?? [];
    return permissions.some((permission: any) => {
      const name = typeof permission === 'string' ? permission : permission?.name;
      return this.managerPermissions.includes(name);
    });
  }

  private extractUserId(user: any): string | null {
    const identifier = user?.user_id ?? user?.userId ?? user?.id;
    return identifier ? String(identifier) : null;
  }

  private restoreMilestoneState(): void {
    try {
      const raw = localStorage.getItem(this.milestoneStorageKey);
      if (!raw) {
        return;
      }
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        parsed.forEach((value) => {
          if (value) {
            this.notifiedMilestoneUserIds.add(String(value));
          }
        });
      } else if (parsed && typeof parsed === 'object') {
        Object.keys(parsed).forEach((key) => {
          if (key) {
            this.notifiedMilestoneUserIds.add(String(key));
          }
        });
      }
    } catch (error) {
      console.warn('Failed to restore leaderboard milestone state.', error);
    }
  }

  private persistMilestoneState(): void {
    localStorage.setItem(
      this.milestoneStorageKey,
      JSON.stringify(Array.from(this.notifiedMilestoneUserIds)),
    );
  }

  private markMilestoneNotified(userId: string): void {
    if (!userId) {
      return;
    }
    this.notifiedMilestoneUserIds.add(userId);
    this.pendingMilestones.delete(userId);
    this.persistMilestoneState();
  }

}
